'use client'
import { useEffect, useState, useMemo, type FC } from 'react'
import { useParams, useRouter } from 'next/navigation'
import { useForm, type SubmitHandler } from 'react-hook-form'
import styled from 'styled-components'
import { useTranslator, useSession } from '@/hooks'
import { autoComplete, inputPattern, cookie } from '@/utils'
import { Button } from '..'
import { Input, Checkbox } from '../inputs'
import { SecondaryNav } from 'src/components/layout/SecondaryNav'
import { MODALS } from '@/context'
import { useModal } from '@/hooks'
import { mq, vw  } from '@/styles'
import { GraphQlErrors } from './GraphQlErrors'
import { DisplayError } from './DisplayError'

type TypeFormInputs = {
  name: string
  email: string
  password: string,
  acceptTerms: boolean
}

function getExpiryDate(): string {
  const date = new Date()
  date.setHours(date.getHours() + 6)
  return date.toISOString()
}

export const FormAuthSignUp: FC = () => {
  const { register, handleSubmit, formState: { errors: formErrors }, setValue, watch } = useForm<TypeFormInputs>()
  const { payload } = useModal(MODALS.AUTH)
  const { lang } = useParams()
  const { t } = useTranslator()
  const router = useRouter()
  const { setLoaderActive } = useSession()
  const watchAcceptTerms = watch('acceptTerms')
  const watchEmail = watch('email')
  const watchPassword = watch('password')
  const [apiErrors, setApiErrors] = useState(null)
  const anchorComponent = useMemo(() => <a href='/rules-and-privacy'>{t('commons.actions.read')}</a>, [t])

  useEffect(() => {
    if(payload) {
      setValue('email', payload)
    }
  }, [payload, setValue])

  const onSubmit: SubmitHandler<TypeFormInputs> = async (formData) => {
    setLoaderActive(true)
    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...formData, lang })
      })

      if (!res.ok) throw new Error('FormAuthSignup submit failed')
      if (res.status === 200) {
        const data = await res.json()
        if(Array.isArray(data.errors) && data.errors.length > 0) {
          console.log('data.errors', data.errors)
          setLoaderActive(false)
          setApiErrors( data.errors)
          return
        }

        if (data?.data?.userRegister && data.data.userRegister?.ok) {
          setApiErrors(null)
          const { token, expiryDate } = data.data.userRegister
          const expiryDateUpdated =  typeof expiryDate === 'string' ? expiryDate : getExpiryDate()
          const isHidden = localStorage.getItem('isHidden')
          if(isHidden) localStorage.removeItem('isHidden')
          cookie.set('DY_AICAM_USER', token, { maxAge: getMaxAge(expiryDateUpdated) })
        }
        setLoaderActive(false)
        alert(t('auth.signup.feedback_success'))
        router.push(`/${lang}/projects?signup=true`)
      }
    } catch (e) {
      setLoaderActive(false)
      setApiErrors(e)
      console.warn('Login submit - Catch e', e)
    }
  }

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <legend><h1>{t('auth.signup.title')}</h1></legend>
        <SecondaryNav />
        <ErrorsWrapper>
          {apiErrors && <GraphQlErrors errors={apiErrors} />}
          {formErrors &&  <DisplayError errors={formErrors} />}
        </ErrorsWrapper>
        <Input type='email' name='email' register={register} label={t('form.fields.email') as string} placeholder='email' autoComplete={autoComplete.email} required={{
          required: t('form.errors.pattern_email') as string,
          pattern: inputPattern.email
        }} error={formErrors.email} />
        <Input type='password' name='password' register={register} label={t('form.fields.new_password') as string} placeholder='password' autoComplete={autoComplete.password.current} icon={'password'} required={{
          required: t('form.errors.pattern_password') as string,
          pattern: inputPattern.password
        }} />
        <Checkbox key='acceptTerms' name='acceptTerms' register={register} label={t('commons.actions.accept_terms') as string} bgColor={'white'}  required={true} error={formErrors.acceptTerms} switchAppearance={true}  linkText={anchorComponent}/>
        <Button disabled={!watchAcceptTerms || !watchEmail || !watchPassword} type='submit' text={t('commons.actions.signup') as string} />
      </Form>
    </>
  )
}

const Form = styled.form`
  width: 90%;
`

const ErrorsWrapper = styled.ul`
  margin: 0 auto ${vw(20, 'mobile')};
  padding: 0 ${ vw(4.25, 'mobile') };
  width: 95%;

  ${mq.greaterThan('nexus7')} {
    margin: 0 auto ${vw(20, 'tablet')};
    padding: 0 ${ vw(4.25, 'tablet') };
  }

  ${mq.greaterThan('tablet') } {
    margin: 0 auto ${vw(30, 'desktop')};
    padding: 0 ${vw(12.5, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    margin: 0 auto 30px;
    padding: 0 12.5px;
  }
`

const getMaxAge = (dateString: string): number => {
  // Parse the date string into a Date object
  const date = new Date(dateString)

  // Calculate the time difference between the parsed date and the current date
  const currentTime = new Date()
  const timeDifferenceMilliseconds = date.getTime() - currentTime.getTime()

  // Convert the time difference to seconds for maxAge
  const maxAgeSeconds = Math.floor(timeDifferenceMilliseconds / 1000)
  return maxAgeSeconds
}
